import { apiClient, downloadFile } from './client';

const BASE = '/api/reports/sessions';
const EVENT_SESSION_PAGE_SIZE = 200;
const MAX_EVENT_SESSION_PAGES = 100;

export const sessionsApi = {
  list: async (params = {}, config = {}) => {
    const response = await apiClient.get(`${BASE}/`, { ...config, params });
    return response.data;
  },

  /** Distinct values for the list filter dropdowns. */
  facets: async (config = {}) => {
    const response = await apiClient.get(`${BASE}/facets`, config);
    return response.data;
  },

  /** Every session of an event (compact), fetched page by page; the backend caps a page at 200. */
  listAllByEvent: async (eventId) => {
    const all = [];
    for (let page = 0; page < MAX_EVENT_SESSION_PAGES; page += 1) {
      const response = await apiClient.get(`${BASE}/`, {
        params: {
          limit: EVENT_SESSION_PAGE_SIZE,
          skip: page * EVENT_SESSION_PAGE_SIZE,
          compact: true,
          include_total: true,
          event_id: eventId,
        },
      });
      const list = Array.isArray(response.data?.sessions) ? response.data.sessions : [];
      all.push(...list);
      const total = response.data?.total;
      if (list.length < EVENT_SESSION_PAGE_SIZE || (Number.isFinite(total) && all.length >= total)) break;
    }
    return all;
  },

  /** `params.lang` returns the report fields of that translation; `params.compact` skips them. */
  get: async (id, params = {}, config = {}) => {
    const response = await apiClient.get(`${BASE}/${id}`, { ...config, params });
    return response.data;
  },

  /** Slim metadata for the transcript editor (no report fields). */
  getEditMeta: async (id) => {
    const response = await apiClient.get(`${BASE}/${id}/edit-meta`);
    return response.data;
  },

  create: async (sessionData) => {
    const response = await apiClient.post(`${BASE}/`, sessionData);
    return response.data;
  },

  /** With `lang`, the report fields are saved to that translation. */
  update: async (id, payload, lang = null) => {
    const response = await apiClient.put(`${BASE}/${id}`, payload, lang ? { params: { lang } } : undefined);
    return response.data;
  },

  remove: async (id) => {
    await apiClient.delete(`${BASE}/${id}`);
  },

  startTranscript: async (id, provider = 'whisper-localhost', transcriptionLanguage = 'en') => {
    const response = await apiClient.post(`${BASE}/${id}/start-transcript`, {
      provider,
      transcription_language: transcriptionLanguage,
    });
    return response.data;
  },

  /** Deletes the current transcript so it can be generated again. */
  regenerateTranscript: async (id) => {
    const response = await apiClient.post(`${BASE}/${id}/regenerate-transcript`);
    return response.data;
  },

  detectTranscriptGaps: async (id, minGapSeconds = 10) => {
    const response = await apiClient.get(`${BASE}/${id}/transcript-gaps`, {
      params: { min_gap_seconds: minGapSeconds },
    });
    return response.data;
  },

  regenerateSection: async (id, startTime, endTime, provider = null, language = 'en') => {
    const response = await apiClient.post(`${BASE}/${id}/regenerate-section`, {
      start_time: startTime,
      end_time: endTime,
      provider,
      language,
    });
    return response.data;
  },

  regenerateAllSections: async (id, gaps, provider = null, language = 'en') => {
    const response = await apiClient.post(`${BASE}/${id}/regenerate-all-sections`, {
      gaps: gaps.map((g) => ({ start_time: g.start_time, end_time: g.end_time })),
      provider,
      language,
    });
    return response.data;
  },

  getTaskStatus: async (taskId) => {
    const response = await apiClient.get(`${BASE}/task-status/${taskId}`);
    return response.data;
  },

  getChunkProgress: async (id) => {
    const response = await apiClient.get(`${BASE}/${id}/chunk-progress`);
    return response.data;
  },

  downloadMedia: async (id) => {
    const session = await sessionsApi.get(id);
    await downloadFile(`${BASE}/${id}/video`, {
      filename: session.original_filename || session.originalFilename || `session_${id}_media`,
    });
  },
};
